import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FilterTabs from '../../components/ui/FilterTabs';
import Amount from '../../components/ui/Amount';
import { shortLabel } from '../../constants/farmMetrics';

const WINDOWS = [
  { id: 'all', label: 'All time' },
  { id: '30d', label: 'Last 30 days' },
  { id: '7d',  label: 'Last 7 days' },
];

function duration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

/** Az első három helyezés kap kiemelést, a többi semleges. */
const RANK_STYLES = {
  1: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  2: 'bg-white/[0.10] text-gray-200 border-white/[0.16]',
  3: 'bg-orange-500/12 text-orange-300 border-orange-400/25',
};

export default function LeaderboardTab() {
  const [metricKey, setMetricKey] = useState('gold');
  const [windowKey, setWindowKey] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/farm/leaderboard', {
        params: { metric: metricKey, window: windowKey, limit: 50 },
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [metricKey, windowKey]);

  useEffect(() => { load(); }, [load]);

  const metrics = data?.metrics ?? [];
  const metric = data?.metric;
  const entries = data?.entries ?? [];

  return (
    <div className="space-y-4">
      {/* Miért látja ezt csak a stáb — hogy a következő fejlesztő ne találgasson */}
      <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg text-sm
                      bg-sky-500/10 border border-sky-500/25 text-sky-300">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 mt-0.5">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-5M12 8h.01" />
        </svg>
        <span className="leading-relaxed">
          Staff only for now. The numbers come from the bot, so they can be faked —
          decide that (and whether users can opt out) before making this public.
        </span>
      </div>

      <PanelCard title="Farming leaderboard" subtitle="Ranked by what each account collected.">
        <div className="space-y-3">
          <div>
            <label className="label">Metric</label>
            <div className="flex flex-wrap gap-1.5">
              {metrics.map(m => (
                <button
                  key={m.key}
                  onClick={() => setMetricKey(m.key)}
                  className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs
                              font-medium border transition-all duration-200 ease-smooth ${
                    metricKey === m.key
                      ? 'bg-[#23262b] text-white border-line-strong'
                      : 'bg-transparent border-line text-gray-400 hover:text-white hover:border-line-hover'
                  }`}
                >
                  <img src={`/icons/${m.icon}`} alt="" title={m.label}
                       className="w-4 h-4 object-contain" />
                  {shortLabel(m)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Period</label>
            <FilterTabs filters={WINDOWS} current={windowKey} onChange={setWindowKey} />
          </div>
        </div>
      </PanelCard>

      <PanelCard
        flush
        title={metric ? `Top by ${metric.label}` : 'Top'}
        subtitle={`${entries.length} account(s) with data in this period.`}
      >
        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></>}
            title="Nothing reported in this period."
            hint="Sessions appear once the bot starts sending stats."
          />
        ) : (
          <div className="scroll-x">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                <tr>
                  <th className="text-left px-5 py-3 table-head w-16">#</th>
                  <th className="text-left px-5 py-3 table-head">Account</th>
                  <th className="text-left px-5 py-3 table-head">Collected</th>
                  <th className="text-left px-5 py-3 table-head hidden sm:table-cell">Spent</th>
                  <th className="text-left px-5 py-3 table-head hidden md:table-cell">Sessions</th>
                  <th className="text-left px-5 py-3 table-head hidden md:table-cell">Time</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.username} className="table-row">
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg
                                        border text-xs font-bold tabular-nums ${
                        RANK_STYLES[e.rank] || 'bg-white/[0.03] text-gray-500 border-white/[0.06]'
                      }`}>
                        {e.rank}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-medium text-white">{e.username}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {metric && (
                          <img src={`/icons/${metric.icon}`} alt=""
                               className="w-4 h-4 object-contain" />
                        )}
                        <Amount value={e.earned} metric={metric}
                                className="text-emerald-300 font-semibold tabular-nums" />
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell tabular-nums">
                      {e.spent > 0
                        ? <Amount value={e.spent} metric={metric} sign="−" className="text-red-400" />
                        : <span className="text-gray-700">—</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-400 tabular-nums hidden md:table-cell">
                      {e.sessionCount}
                    </td>
                    <td className="px-5 py-3 text-gray-500 tabular-nums hidden md:table-cell">
                      {duration(e.totalSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelCard>
    </div>
  );
}
